import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useCallback, useRef } from "react";

export default function useUrlParams() {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const debounceTimeouts = useRef<Record<string, NodeJS.Timeout>>({});

  const updateURLParams = useCallback(
    (updates: Record<string, string | null>, debounceMs?: number) => {
      const performUpdate = () => {
        const params = new URLSearchParams(searchParams);

        Object.entries(updates).forEach(([key, value]) => {
          if (value === null || value === "") {
            params.delete(key);
          } else {
            params.set(key, value);
          }
        });

        const queryString = params.toString();
        const url = queryString ? `${pathname}?${queryString}` : pathname;
        router.replace(url, { scroll: false });
      };

      if (debounceMs && debounceMs > 0) {
        const updateKey = Object.keys(updates).sort().join(",");

        if (debounceTimeouts.current[updateKey]) {
          clearTimeout(debounceTimeouts.current[updateKey]);
        }

        debounceTimeouts.current[updateKey] = setTimeout(() => {
          performUpdate();
          delete debounceTimeouts.current[updateKey];
        }, debounceMs);
      } else {
        performUpdate();
      }
    },
    [router, pathname, searchParams],
  );

  const clearAllParams = useCallback(() => {
    Object.values(debounceTimeouts.current).forEach(clearTimeout);
    debounceTimeouts.current = {};

    router.replace(pathname, { scroll: false });
  }, [router, pathname]);

  return {
    updateURLParams,
    clearAllParams,
    searchParams,
    pathname,
  };
}
